import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Camera,
  ImagePlus,
  Loader2,
  Maximize2,
  Trash2,
} from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const SEGMENTS = 72;
const GROUP_SIZE = 4;
const NUM_GROUPS = SEGMENTS / GROUP_SIZE;
const DEG_PER_GROUP = 360 / NUM_GROUPS;
/** SVG: 0° = 3 o'clock. Index 0 = front/top = -90° in math. */
const GROUP_TO_START_DEG = (g: number) => -90 + g * DEG_PER_GROUP;

export interface ImageByIndex {
  id: string;
  url: string;
  /** Full-size URL for fullscreen (optional; falls back to url) */
  urlFull?: string;
}

interface Exterior360SegmentPickerProps {
  imagesByIndex: Record<number, ImageByIndex>;
  carTopViewUrl?: string;
  labels?: {
    selectOrTake?: string;
    uploadImage?: string;
    openCamera?: string;
    index?: string;
    imageLabel?: string;
    deleteImage?: string;
    viewFullscreen?: string;
    segmentViewFullscreen?: string;
    segmentUploaded?: string;
    segmentEmpty?: string;
    segmentPartial?: string;
    noImageFound?: string;
    groupFrames?: string;
  };
  onUpload: (frameIndex: number, file: File) => void;
  onOpenCamera: (frameIndex: number) => void;
  onDelete?: (frameIndex: number, imageId: string) => void;
  onViewFullscreen?: (
    frameIndex: number,
    url: string,
    urlFull?: string,
  ) => void;
  readOnly?: boolean;
  transparent?: boolean;
  fill?: boolean;
  uploadingFrameIndices?: number[];
  deletingFrameIndices?: number[];
  /** When set, shows overlay with progress during batch upload (e.g. after video extract) */
  uploadProgress?: { done: number; total: number; failed?: number };
  uploadProgressLabels?: {
    generating?: string;
    progressPie?: string;
    progressPercent?: string;
    timeEstimate?: string;
    complete?: string;
  };
}

/** Creates SVG path for a pie slice. */
function wedgePath(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): string {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const x = (deg: number) => cx + r * Math.cos(toRad(deg));
  const y = (deg: number) => cy + r * Math.sin(toRad(deg));
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const sweep = endDeg > startDeg ? 1 : 0;
  return `M ${cx} ${cy} L ${x(startDeg)} ${y(startDeg)} A ${r} ${r} 0 ${large} ${sweep} ${x(endDeg)} ${y(endDeg)} Z`;
}

export function Exterior360SegmentPicker({
  imagesByIndex,
  carTopViewUrl = "/assets/car_silhouttes/car_top_view.png",
  labels = {},
  onUpload,
  onOpenCamera,
  onDelete,
  onViewFullscreen,
  readOnly = false,
  transparent = false,
  fill = false,
  uploadingFrameIndices = [],
  deletingFrameIndices = [],
  uploadProgress,
  uploadProgressLabels = {},
}: Exterior360SegmentPickerProps) {
  const pendingFrameRef = useRef(0);
  const openFilePickerRef = useRef<(() => void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState<number | null>(null);
  const [previewCoords, setPreviewCoords] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUploadClick = useCallback((frameIndex: number) => {
    pendingFrameRef.current = frameIndex;
    openFilePickerRef.current?.();
  }, []);

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (file && file.type.startsWith("image/")) {
        onUpload(pendingFrameRef.current, file);
      }
    },
    [onUpload],
  );

  const size = 280;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;

  const groupPaths = useMemo(() => {
    return Array.from({ length: NUM_GROUPS }, (_, g) => ({
      groupIndex: g,
      path: wedgePath(
        cx,
        cy,
        r,
        GROUP_TO_START_DEG(g),
        GROUP_TO_START_DEG(g + 1),
      ),
    }));
  }, []);

  const labelPositions = useMemo(() => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const rLabel = 118;
    return Array.from({ length: NUM_GROUPS }, (_, g) => {
      const midDeg = GROUP_TO_START_DEG(g) + DEG_PER_GROUP / 2;
      return {
        groupIndex: g,
        x: cx + rLabel * Math.cos(toRad(midDeg)),
        y: cy + rLabel * Math.sin(toRad(midDeg)),
        label: `${g * GROUP_SIZE + 1}-${g * GROUP_SIZE + GROUP_SIZE}`,
      };
    });
  }, []);

  const groupFramesLabel = labels.groupFrames ?? "Rutor";
  const showUploadOverlay =
    uploadProgress &&
    uploadProgress.total > 0 &&
    uploadProgress.done < uploadProgress.total;
  const genLabel =
    uploadProgressLabels.generating ?? "Genererar 360-vy";
  const progressPercentLabel =
    uploadProgressLabels.progressPercent ?? "{percent}% uppladdat";
  const timeEstLabel =
    uploadProgressLabels.timeEstimate ??
    "Kan ta upp till 1–2 minuter beroende på nätverkshastighet.";
  const uploadLabel = labels.uploadImage ?? "Ladda upp bild";
  const cameraLabel = labels.openCamera ?? "Öppna kamera";
  const imageLabel = labels.imageLabel ?? "Bild";
  const deleteLabel = labels.deleteImage ?? "Radera bild";
  const uploadedLabel = labels.segmentUploaded ?? "Uppladdad";
  const emptyLabel = labels.segmentEmpty ?? "Tom";
  const partialLabel = labels.segmentPartial ?? "Delvis";
  const noImageFoundLabel = labels.noImageFound ?? "Ingen bild hittad";

  const uploadingSet = useMemo(
    () => new Set(uploadingFrameIndices),
    [uploadingFrameIndices],
  );
  const deletingSet = useMemo(
    () => new Set(deletingFrameIndices),
    [deletingFrameIndices],
  );

  const getGroupStatus = useCallback(
    (groupIndex: number) => {
      const start = groupIndex * GROUP_SIZE;
      let filled = 0;
      let uploading = 0;
      let deleting = 0;
      for (let i = 0; i < GROUP_SIZE; i++) {
        const idx = start + i;
        if (imagesByIndex[idx] != null) filled++;
        if (uploadingSet.has(idx)) uploading++;
        if (deletingSet.has(idx)) deleting++;
      }
      return { filled, total: GROUP_SIZE, uploading, deleting };
    },
    [imagesByIndex, uploadingSet, deletingSet],
  );

  const pathEl = (
    groupIndex: number,
    p: string,
    status: { filled: number; total: number },
  ) => {
    const allFilled = status.filled === status.total;
    const someFilled = status.filled > 0;
    return (
      <path
        d={p}
        className={cn(
          "transition-colors",
          allFilled
            ? "fill-emerald-600/70 stroke-emerald-700 hover:fill-emerald-500"
            : someFilled
              ? "fill-amber-500/50 stroke-amber-600 hover:fill-amber-500/70"
              : "fill-slate-500/20 stroke-slate-400/40 hover:fill-slate-500/35 hover:stroke-slate-400/60",
        )}
        strokeWidth="1.5"
        style={{ pointerEvents: "auto" }}
      />
    );
  };

  const groupLabel = (g: number, px: number, py: number, label: string) => {
    const status = getGroupStatus(g);
    const isUploading = status.uploading > 0;
    const isDeleting = status.deleting > 0;
    return (
      <foreignObject
        key={`label-${g}`}
        x={px - 24}
        y={py - 10}
        width={48}
        height={20}
        className="overflow-visible"
        style={{ pointerEvents: "none" }}
      >
        <div className="flex items-center justify-center gap-1 text-[10px] font-medium text-foreground/90">
          {isUploading || isDeleting ? (
            <Loader2 className="h-2.5 w-2.5 shrink-0 animate-spin" strokeWidth={2.5} />
          ) : null}
          <span>{label}</span>
        </div>
      </foreignObject>
    );
  };

  const handleGroupMouseEnter = useCallback(
    (g: number) => {
      if (groupDialogOpen != null) return;
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredGroup(g);
        const el = containerRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          setPreviewCoords({ x: rect.left + rect.width / 2, y: rect.top });
        }
      }, 120);
    },
    [groupDialogOpen],
  );

  const handleGroupMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredGroup(null);
    setPreviewCoords(null);
  }, []);

  useEffect(() => {
    if (hoveredGroup != null && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPreviewCoords({ x: rect.left + rect.width / 2, y: rect.top });
    }
  }, [hoveredGroup]);

  /** Hover preview: all available images from the group, each with badge */
  const hoveredGroupImages =
    hoveredGroup != null
      ? Array.from({ length: GROUP_SIZE }, (_, i) => {
          const idx = hoveredGroup * GROUP_SIZE + i;
          const img = imagesByIndex[idx];
          return img ? { index: idx, url: img.url } : null;
        }).filter(Boolean) as { index: number; url: string }[]
      : [];

  const renderFrameInGroup = (frameIndex: number) => {
    const img = imagesByIndex[frameIndex];
    const hasImg = img != null;
    const isUploading = uploadingSet.has(frameIndex);
    const isDeleting = deletingSet.has(frameIndex);

    return (
      <div
        key={frameIndex}
        className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800"
      >
        <p className="text-xs font-medium text-muted-foreground px-2 py-1">
          {imageLabel} {frameIndex + 1}
        </p>
        {hasImg && img ? (
          <div className="p-2 space-y-2">
            <div className="rounded overflow-hidden aspect-video bg-slate-200 dark:bg-slate-700">
              {isUploading || isDeleting ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-full object-contain"
                />
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {onViewFullscreen && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={() => onViewFullscreen(frameIndex, img.url, img.urlFull)}
                  title={labels.viewFullscreen ?? labels.segmentViewFullscreen ?? "Visa i helskärm"}
                  aria-label={labels.viewFullscreen ?? labels.segmentViewFullscreen ?? "Visa i helskärm"}
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
              )}
              {onDelete && !img.id.startsWith("pending-") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive h-8 w-8 p-0 shrink-0"
                  onClick={() => onDelete(frameIndex, img.id)}
                  disabled={isDeleting}
                  title={deleteLabel}
                  aria-label={deleteLabel}
                >
                  {isDeleting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 shrink-0" />
                  )}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-2">
            <p className="text-xs text-muted-foreground mb-2">
              {noImageFoundLabel}
            </p>
            {!readOnly && (
              <div className="flex flex-col gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start h-8 text-xs"
                  onClick={() => onOpenCamera(frameIndex)}
                >
                  <Camera className="h-3 w-3 mr-1 shrink-0" />
                  {cameraLabel}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start h-8 text-xs"
                  onClick={() => handleUploadClick(frameIndex)}
                >
                  <ImagePlus className="h-3 w-3 mr-1 shrink-0" />
                  {uploadLabel}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-full",
        fill ? "max-w-none" : "max-w-[320px] mx-auto",
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden",
          transparent ? "" : "rounded-2xl bg-slate-100 dark:bg-slate-800",
        )}
        style={{ aspectRatio: "1" }}
      >
        <svg
          viewBox="0 0 280 280"
          className="absolute inset-0 w-full h-full cursor-pointer"
          style={{ pointerEvents: "none" }}
        >
          <g style={{ pointerEvents: "auto" }}>
            {groupPaths.map(({ groupIndex, path }) => {
              const status = getGroupStatus(groupIndex);
              return (
                <g
                  key={groupIndex}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => handleGroupMouseEnter(groupIndex)}
                  onMouseLeave={handleGroupMouseLeave}
                  onClick={() => setGroupDialogOpen(groupIndex)}
                >
                  {pathEl(groupIndex, path, status)}
                </g>
              );
            })}
          </g>
          <g style={{ pointerEvents: "none" }}>
            {labelPositions.map(({ groupIndex, x, y, label }) =>
              groupLabel(groupIndex, x, y, label),
            )}
          </g>
        </svg>
        <img
          src={carTopViewUrl}
          alt="Bil vy från ovan"
          className="absolute inset-0 w-full h-full object-contain p-4 pointer-events-none z-10"
        />
        {showUploadOverlay && uploadProgress && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl bg-slate-900/90 backdrop-blur-sm">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-3" />
            <p className="text-sm font-semibold text-white mb-1">{genLabel}</p>
            <p className="text-xs text-slate-300 mb-2">
              {progressPercentLabel.replace(
                "{percent}",
                String(
                  uploadProgress.total > 0
                    ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
                    : 0
                )
              )}
            </p>
            <div className="w-4/5 max-w-[200px] h-2 rounded-full bg-slate-700 overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{
                  width: `${(uploadProgress.done / uploadProgress.total) * 100}%`,
                }}
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-2 px-4 text-center">
              {timeEstLabel}
            </p>
          </div>
        )}
      </div>

      {hoveredGroupImages.length > 0 && previewCoords &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              left: previewCoords.x,
              top: previewCoords.y,
              transform: "translate(-50%, calc(-100% - 12px))",
            }}
            aria-hidden
          >
            <div className="flex gap-1.5 p-1.5 rounded-lg shadow-2xl ring-2 ring-black/15 bg-background">
              {hoveredGroupImages.map(({ index, url }) => (
                <div key={index} className="relative">
                  <img
                    src={url}
                    alt=""
                    className="w-20 h-14 object-cover rounded border border-slate-200 dark:border-slate-600"
                  />
                  <span className="absolute top-0.5 right-0.5 px-1 py-0.5 text-[9px] font-medium rounded bg-black/60 text-white">
                    {index + 1}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-center mt-1 text-muted-foreground">
              {groupFramesLabel} {hoveredGroup! * GROUP_SIZE + 1}–
              {hoveredGroup! * GROUP_SIZE + GROUP_SIZE}
            </p>
          </div>,
          document.body,
        )}

      <FileUploadTrigger accept="image/*" onFilesSelected={handleFilesSelected}>
        {(open) => {
          openFilePickerRef.current = open;
          return null;
        }}
      </FileUploadTrigger>

      <Dialog
        open={groupDialogOpen != null}
        onOpenChange={(open) => !open && setGroupDialogOpen(null)}
      >
        <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {groupDialogOpen != null &&
                `${groupFramesLabel} ${groupDialogOpen * GROUP_SIZE + 1}–${groupDialogOpen * GROUP_SIZE + GROUP_SIZE}`}
            </DialogTitle>
          </DialogHeader>
          {groupDialogOpen != null && (
            <div className="grid grid-cols-2 gap-3 py-2">
              {Array.from({ length: GROUP_SIZE }, (_, i) =>
                renderFrameInGroup(groupDialogOpen * GROUP_SIZE + i),
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-emerald-600 ring-1 ring-emerald-700" />
          {uploadedLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-amber-500/50 ring-1 ring-amber-600" />
          {partialLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-slate-500/20 ring-1 ring-slate-400/40" />
          {emptyLabel}
        </span>
      </div>
    </div>
  );
}
